import './style.scss'
import { Button, Checkbox, message, Modal, QRCode, Select } from 'antd'
import { FC, memo, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Api from '@/api/account'
import * as HomeApi from '@/api/home'
import { UserStore } from '@/global-states'
import { eventTracking, showPlanModal } from '@/libs/util'
import { AlipayOutlined, RightOutlined, WechatOutlined } from '@ant-design/icons'
import UserModalHeader from '../UserModalHeader'

interface Iil {
  credits: number
  discounted_price: number
  id: number
  original_price: number
  badge?: {
    background_color: string
    content: string
    content_color: string
  }
}

interface IProps {
  open: boolean
  onCancel?: () => void
}

const PayPointModal: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['pay_point_modal'])
  const { open, onCancel } = props
  const { userPackage } = UserStore
  const [payMethod, setPayMethod] = useState(1)
  const [oilProduct, setOilProduct] = useState<Iil[]>()
  const [checkedProduct, setCheckedProduct] = useState<Iil>()
  const [loading, setLoading] = useState(false)
  const [payUrl, setPayUrl] = useState('')
  const [checked, setChecked] = useState(true)
  const payTimer = useRef<any>()
  const [couponList, setCouponList] = useState<any[]>([])
  const [coupon, setCoupon] = useState<any>()

  const vip = useMemo(() => {
    return !!userPackage?.membership_expire_time
  }, [userPackage])

  const vipExpired = useMemo(() => {
    return !!userPackage?.membership_expired
  }, [userPackage])

  useEffect(() => {
    setPayMethod(1)
    setPayUrl('')
    clearPayTimer()
    getClaimedCoupons()
    if (open) {
      getProduct()
      eventTracking('PointModalOpen')
    }
  }, [open])

  const getProduct = async () => {
    const { list }: { list: Iil[] } = await Api.getPermanentProductList()
    setOilProduct(list || [])
    setCheckedProduct(list?.[0])
  }

  const getClaimedCoupons = async () => {
    const res = await HomeApi.getClaimedCoupons({
      usage: 2,
      status: 2
    })
    const list = res?.list || []
    const selectOptions = [
      {
        label: translate('pay_point_modal.no_coupon'),
        value: -1,
        id: -1,
        deduct_amount: 0
      },
      ...(list.map((l: any) => ({
        ...l,
        label: l.title,
        value: l.id
      })) || [])
    ]
    setCouponList(selectOptions)
    const maxItem = selectOptions.reduce((max: any, item: any) => (item.deduct_amount > max.deduct_amount ? item : max))
    if (maxItem) {
      setCoupon(maxItem)
    }
  }

  const changePayMethod = (method: number) => {
    setPayMethod(method)
  }

  const toPay = async () => {
    if (!vip) {
      return Modal.confirm({
        title: translate('pay_point_modal.tip_title'),
        content: translate('pay_point_modal.free_user_tip'),
        okText: translate('pay_point_modal.become_member'),
        onOk: () => {
          onCancel?.()
          showPlanModal()
        }
      })
    } else if (vipExpired) {
      return Modal.confirm({
        title: translate('pay_point_modal.tip_title'),
        content: translate('pay_point_modal.expired_member_tip'),
        okText: translate('pay_point_modal.renew_member'),
        onOk: () => {
          onCancel?.()
          showPlanModal()
        }
      })
    }

    setLoading(true)

    try {
      const params: any = {
        package_id: checkedProduct?.id,
        client_type: 1
      }
      if (coupon?.id && coupon?.id !== -1) {
        params.coupon_id = coupon.id
      }
      const { pay_order_id, completed } = await Api.permanentOrderCreate(params)

      if (completed) {
        message.success(translate('pay_point_modal.payment_success'))
        onCancel?.()
        UserStore.updatePayStatus()
        UserStore.updateUserPackage()
        return
      }

      const buyParams = {
        orderId: pay_order_id,
        payType: payMethod === 0 ? 'ALIPAY' : 'WECHATPAY'
      }

      eventTracking('CreditsPayNow', buyParams)

      const buyRes = await Api.paymentInit(buyParams)
      UserStore.updatePayStatus()
      if (payMethod === 1) {
        setPayUrl(buyRes?.pay_list.wechatpayNative?.code_url)
        paymentCheck(pay_order_id)
        payTimer.current = setInterval(() => {
          paymentCheck(pay_order_id)
        }, 3000)
      } else {
        location.href = buyRes?.pay_list?.alipay
      }
    } finally {
      setLoading(false)
    }
  }

  const paymentCheck = async (order_id: string) => {
    const res = await Api.paymentCheck({
      order_id
    })
    if (res?.is_pay === 1) {
      message.success(translate('pay_point_modal.payment_success'))
      clearPayTimer()
      onCancel?.()
      UserStore.updatePayStatus()
      UserStore.updateUserPackage()
    }
  }

  const clearPayTimer = () => {
    if (payTimer.current) {
      clearInterval(payTimer.current)
      payTimer.current = undefined
    }
  }

  return (
    <Suspense>
      <Modal className="pay-point-modal" footer={null} width={660} onCancel={() => onCancel?.()} open={open}>
        <div className="pay-content">
          <UserModalHeader />
          <div className="pay-content-main">
            {!payUrl ? (
              <>
                <div className="plan">
                  {oilProduct?.map((o) => (
                    <div
                      className={`plan-item ${checkedProduct?.id === o.id ? 'actived' : ''}`}
                      key={o.id}
                      onClick={() => setCheckedProduct(o)}
                    >
                      <div className="score">
                        {o.credits}
                        {translate('pay_point_modal.points')}
                      </div>
                      <div className="price">
                        <div className={`discount-price ${checkedProduct?.id === o.id ? 'gradient' : ''}`}>
                          <label className="unit">¥</label>
                          {(o.discounted_price || 0) / 100}
                        </div>
                        {o.original_price !== o.discounted_price ? (
                          <div className="origin-price">¥{(o.original_price || 0) / 100}</div>
                        ) : (
                          <div className="origin-price"></div>
                        )}
                      </div>
                      {!!o.badge && (
                        <div className="tag">
                          <label className="gradient">{o.badge.content}</label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pay-rules">
                  <div className="name">
                    <a
                      className="gradient"
                      target="_blank"
                      href="https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0"
                      rel="noreferrer"
                    >
                      {translate('pay_point_modal.points_instruction')}
                    </a>
                    <span className="text">{translate('pay_point_modal.points_usage')}</span>
                  </div>
                </div>

                <div className="pay-methods">
                  <div className="name">{translate('pay_point_modal.payment_method')}</div>
                  <div className="pay-list">
                    <div
                      className={`item wechat ${payMethod === 1 ? 'actived' : ''}`}
                      onClick={changePayMethod.bind(this, 1)}
                    >
                      <WechatOutlined />
                      <div>{translate('pay_point_modal.wechat_pay')}</div>
                    </div>
                    <div
                      className={`item alipay ${payMethod === 0 ? 'actived' : ''}`}
                      onClick={changePayMethod.bind(this, 0)}
                    >
                      <AlipayOutlined />
                      <div>{translate('pay_point_modal.alipay')}</div>
                    </div>
                  </div>
                </div>

                <div className="pay-coupon">
                  <div className="name">{translate('pay_point_modal.coupon')}</div>
                  <div className="detail">
                    {coupon?.id === -1 ? (
                      <>
                        <label className="unuse">{translate('pay_point_modal.no_coupon')}</label>
                      </>
                    ) : (
                      <>
                        <label>- ¥ {coupon?.deduct_amount / 100}</label>
                        <label className="desc">{coupon?.title}</label>
                      </>
                    )}

                    <RightOutlined className="arrow-right" />

                    <Select
                      className="select-coupon"
                      value={coupon?.id}
                      options={couponList}
                      onChange={(_, c) => {
                        setCoupon(c)
                      }}
                      defaultValue={-1}
                    ></Select>
                  </div>
                </div>

                <div className="pay">
                  <div className="name">{translate('pay_point_modal.amount_due')}</div>

                  {coupon && coupon.deduct_amount > 0 ? (
                    <div className="money gradient">
                      {(checkedProduct?.discounted_price || 0) > coupon.deduct_amount ? (
                        <label>￥{((checkedProduct?.discounted_price || 0) - coupon.deduct_amount) / 100}</label>
                      ) : (
                        <label>￥0</label>
                      )}
                      <label className="origin-price">￥{(checkedProduct?.discounted_price || 0) / 100}</label>
                    </div>
                  ) : (
                    <div className="money gradient">￥{(checkedProduct?.discounted_price || 0) / 100}</div>
                  )}
                </div>

                <div className="btn">
                  <Button disabled={!checked} loading={loading} type="primary" onClick={toPay}>
                    {translate('pay_point_modal.pay_now')}
                  </Button>
                </div>
                <div className="checkbox">
                  <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}></Checkbox>
                  <div>
                    {translate('pay_point_modal.agreement_text')}
                    <a className="gradient" href="/point_agreement.html" target="_blank">
                      {translate('pay_point_modal.points_agreement')}
                    </a>
                  </div>
                </div>
              </>
            ) : (
              <div className="pay-qrcode">
                <div className="pay-desc">
                  <div>{translate('pay_point_modal.scan_to_pay')}</div>
                  {coupon && coupon.deduct_amount > 0 ? (
                    <div className="mount gradient">
                      {(checkedProduct?.discounted_price || 0) > coupon.deduct_amount ? (
                        <label>￥{((checkedProduct?.discounted_price || 0) - coupon.deduct_amount) / 100}</label>
                      ) : (
                        <label className="gradient">￥0</label>
                      )}
                    </div>
                  ) : (
                    <div className="mount gradient">￥{(checkedProduct?.discounted_price || 0) / 100}</div>
                  )}
                </div>
                <QRCode value={payUrl} />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Suspense>
  )
}

export default memo(PayPointModal)
